import { useState, useMemo } from "react";
import { ArrowLeftRight, Ban, Plane, RefreshCw } from "lucide-react";
import type { Account, DashboardData, EntryPreset, Trip } from "../../types";
import { money, shortName, todayIn } from "../../lib/finance";
import { Private } from "../../components/Privacy";
import FormActions from "../../components/FormActions";
import { inferCurrencyForDestination } from "../../lib/countries";
import { enqueuePendingEntry } from "../../lib/pending-entries";
import { syncPendingEntries } from "../../lib/sync-pending-entries";

export interface AccountTripLeftoverBannerProps {
  account: Account;
  trip?: Trip;
  data: DashboardData;
  onAddTransaction: (
    accountId: string,
    type: "Expense" | "Income" | "Transfer",
    prefill?: Partial<EntryPreset>,
  ) => void;
  onRefresh?: () => Promise<unknown>;
  onClose: () => void;
}

export default function AccountTripLeftoverBanner({
  account,
  trip,
  data,
  onAddTransaction,
  onRefresh,
  onClose,
}: AccountTripLeftoverBannerProps) {
  const isCash = account.type.toLowerCase() === "cash";
  const isTripWallet = isCash && Boolean(trip);
  const isCompletedTrip = trip?.status === "completed" || trip?.status === "archived";
  const isLeftover = isTripWallet && isCompletedTrip && account.balance > 0;

  const [showLostConfirm, setShowLostConfirm] = useState(false);
  const [busyLost, setBusyLost] = useState(false);
  const [lostError, setLostError] = useState("");
  const [writeOffReason, setWriteOffReason] = useState("");

  const upcomingTrips = useMemo(() => {
    return (data.trips || []).filter(
      (t) => t.id !== trip?.id && (t.status === "active" || t.status === "planned"),
    );
  }, [data.trips, trip]);

  const candidateTrip = useMemo(() => {
    if (!upcomingTrips.length) return undefined;
    return (
      upcomingTrips.find((t) =>
        (t.destinations || []).some(
          (d) => inferCurrencyForDestination(d) === account.currency,
        ) ||
        t.accountIds?.some((aid) => {
          const acc = (data.accounts || []).find((a) => a.id === aid);
          return acc && acc.currency === account.currency;
        }) ||
        account.currency === "USD",
      ) || upcomingTrips[0]
    );
  }, [upcomingTrips, account, data.accounts]);

  const targetTripWallet = useMemo(() => {
    if (!candidateTrip) return undefined;
    return (data.accounts || []).find(
      (a) =>
        candidateTrip.accountIds?.includes(a.id) &&
        a.currency === account.currency &&
        a.type.toLowerCase() === "cash",
    );
  }, [candidateTrip, account, data.accounts]);

  const bankAccounts = useMemo(() => {
    return (data.accounts || []).filter(
      (a) => a.active !== false && a.type.toLowerCase() !== "cash" && a.owner === account.owner,
    );
  }, [data.accounts, account.owner]);

  const defaultBank = useMemo(() => {
    return (
      bankAccounts.find((a) => a.currency === account.currency) ||
      bankAccounts.find((a) => a.currency === "USD") ||
      bankAccounts[0]
    );
  }, [bankAccounts, account.currency]);

  function handleRollover() {
    if (!candidateTrip) return;
    onAddTransaction(account.id, "Transfer", {
      tripId: candidateTrip.id,
      tripDestination:
        candidateTrip.destinations.find(
          (d) => inferCurrencyForDestination(d) === account.currency,
        ) || candidateTrip.destinations[0],
      toAccountId: targetTripWallet?.id,
      amount: String(account.balance),
      exchangeRate: "1",
      notes: `Rollover leftover ${account.currency} from ${trip?.name || "trip"}`,
    });
  }

  function handleDeposit() {
    onAddTransaction(account.id, "Transfer", {
      toAccountId: defaultBank?.id,
      amount: String(account.balance),
      notes: `Deposit leftover ${account.currency} from ${trip?.name || "trip"}`,
    });
  }

  async function handleWriteOffLost() {
    setBusyLost(true);
    setLostError("");
    try {
      const reason = writeOffReason.trim();
      const notes = reason
        ? `${reason} · Leftover cash written off from ${trip?.name || "trip"}`
        : `Leftover cash written off from ${trip?.name || "trip"}`;

      enqueuePendingEntry({
        requestId: crypto.randomUUID(),
        type: "Expense",
        accountId: account.id,
        amount: String(account.balance),
        date: todayIn(data.timezone),
        category: "Travel",
        subcategory: "Write-off",
        title: reason ? `Write-off (${reason})` : "Leftover cash write-off",
        notes,
        owner: account.owner,
        tripId: trip?.id,
      });
      await syncPendingEntries();
      if (onRefresh) await onRefresh();
      onClose();
    } catch (e) {
      setLostError((e as Error).message);
    } finally {
      setBusyLost(false);
    }
  }

  function handleEditLostExpense() {
    setShowLostConfirm(false);
    const reason = writeOffReason.trim();
    onAddTransaction(account.id, "Expense", {
      amount: String(account.balance),
      title: reason ? `Write-off (${reason})` : "Leftover cash write-off",
      category: "Travel",
      subcategory: "Write-off",
      notes: reason
        ? `${reason} · Leftover cash written off from ${trip?.name || "trip"}`
        : `Leftover cash written off from ${trip?.name || "trip"}`,
      tripId: trip?.id,
    });
  }

  if (!isLeftover) return null;

  return (
    <section className="leftover-callout">
      <div className="leftover-callout-head">
        <div className="leftover-callout-icon">
          <Plane size={18} />
        </div>
        <div className="leftover-callout-title">
          <strong>Leftover cash from {trip?.name}</strong>
          <span>
            Remaining: <Private>{money(account.balance, account.currency)}</Private>
          </span>
        </div>
      </div>

      <p className="leftover-callout-intro">
        Choose what to do with this remaining cash. Amounts and accounts are pre-selected for you:
      </p>

      <div className="leftover-action-buttons">
        {candidateTrip && (
          <button
            type="button"
            className="leftover-action-btn primary"
            onClick={handleRollover}
          >
            <RefreshCw size={15} />
            <span>
              Roll over <Private>{money(account.balance, account.currency)}</Private> to <strong>{candidateTrip.name}</strong>
            </span>
          </button>
        )}

        {defaultBank && (
          <button
            type="button"
            className="leftover-action-btn secondary"
            onClick={handleDeposit}
          >
            <ArrowLeftRight size={15} />
            <span>
              Deposit full balance to <strong>{shortName(defaultBank.name)}</strong>
            </span>
          </button>
        )}

        <button
          type="button"
          className="leftover-action-btn warning"
          onClick={() => setShowLostConfirm(true)}
        >
          <Ban size={15} />
          <span>Write off remaining cash</span>
        </button>
      </div>

      {showLostConfirm && (
        <form
          className="lost-confirm-box"
          onSubmit={(e) => {
            e.preventDefault();
            void handleWriteOffLost();
          }}
        >
          <div className="lost-confirm-head">
            <Ban size={16} />
            <strong>Write off remaining cash?</strong>
          </div>
          <p>
            This will write off <Private><strong>{money(account.balance, account.currency)}</strong></Private> as an expense (e.g. for lost, spent, or unrecoverable loose cash).
            The wallet balance will become 0 and be automatically settled, closing it so it won’t appear in leftover cash or rollover lists anymore.
          </p>
          <label className="write-off-reason-field">
            <span>Reason / note <small>(optional)</small></span>
            <input
              type="text"
              placeholder="e.g. Airport charity box, lost change, taxi tip"
              value={writeOffReason}
              onChange={(e) => setWriteOffReason(e.target.value)}
              disabled={busyLost}
            />
          </label>
          {lostError && <p className="error-text">{lostError}</p>}
          <FormActions
            label="Write off & settle wallet"
            busy={busyLost}
            busyLabel="Writing off"
            secondary={
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <button
                  type="button"
                  className="form-actions-muted"
                  disabled={busyLost}
                  onClick={handleEditLostExpense}
                >
                  Edit details in expense form
                </button>
                <span style={{ color: "var(--line)" }}>•</span>
                <button
                  type="button"
                  className="form-actions-muted"
                  disabled={busyLost}
                  onClick={() => setShowLostConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            }
          />
        </form>
      )}
    </section>
  );
}
